import { Card, Col, Drawer, List, Row, Spin, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { APP_DATE_FORMAT } from 'config/constants';
import './Dashboard.scss';
import { Link } from 'react-router-dom';
import ProposalChart from '../../components/Charts/ProposalChart';
import RenuewChart from '../../components/Charts/RenuewChart';
import JobTrackingChart from '../../components/Charts/JobTrackingChart';
import RenuewCard from '../../components/Vallet/RenuewCard';
import ContractCountCard from '../../components/Vallet/ContractCountCard';
import SummaryCard from '../../components/Vallet/SummaryCard';
import { hasRole } from '../../utils';
import TicketWidget from '../../modules/Ticket/TicketWidget';
import {
  listLatestRejectedMeasuringInstrumentInspectors,
  listPendingApprovalMeasuringInstrumentInspectors,
} from '../../modules/measuring-instrument-inspectors/services/measuring-instrument-inspectorService';
import FinanceDashboard from '../Finance/FinanceDashboard';
import type { MeasuringInstrumentInspector } from '../../modules/measuring-instrument-inspectors/types/MeasuringInstrumentInspector';
import ReminderSummaryCards from '../../components/Reminders/ReminderSummaryCards';
import ManualFollowProposalsList from '../../components/Proposal/ManualFollowProposalsList';
import OverdueSentProposalsList from '../../components/Proposal/OverdueSentProposalsList';
import LegalNotificationSummaryCard from '../../components/Dashboard/LegalNotificationSummaryCard';
import StatusSummaryCards from '../../components/Dashboard/StatusSummaryCards';
import PlanDeviceReadinessCard from '../../components/Dashboard/PlanDeviceReadinessCard';

const { Text, Title } = Typography;

const Dashboard = () => {

  const [isAdmin, setIsAdmin] = useState(false);
  const [isCentralUser, setIsCentralUser] = useState(false);
  const [isFinance, setIsFinance] = useState(false);
  const [rejectedInspectors, setRejectedInspectors] = useState<MeasuringInstrumentInspector[]>([]);
  const [pendingApprovalInspectors, setPendingApprovalInspectors] = useState<MeasuringInstrumentInspector[]>([]);
  const [isRejectedModalOpen, setIsRejectedModalOpen] = useState(false);
  const [isPendingApprovalModalOpen, setIsPendingApprovalModalOpen] = useState(false);
  const [isRejectedLoading, setIsRejectedLoading] = useState(false);
  const [isPendingApprovalLoading, setIsPendingApprovalLoading] = useState(false);
  const isCustomer = useMemo(() => hasRole('CUSTOMER'), []);
  const canViewLegalNotificationSummary = isAdmin || isCentralUser;

  useEffect(() => {
    const isUserAdmin = hasRole('ADMIN');
    setIsAdmin(isUserAdmin);

    const isUserCentralUser = hasRole('CENTRAL_USER');
    setIsCentralUser(isUserCentralUser);
    setIsFinance(hasRole('FINANCE'));
  }, [])

  useEffect(() => {
    if (!isAdmin) {
      setRejectedInspectors([]);
      setPendingApprovalInspectors([]);
      setIsRejectedModalOpen(false);
      setIsPendingApprovalModalOpen(false);
      return;
    }
    const loadInstrumentApprovalSummaries = async () => {
      setIsRejectedLoading(true);
      setIsPendingApprovalLoading(true);
      try {
        const [rejectedList, pendingList] = await Promise.all([
          listLatestRejectedMeasuringInstrumentInspectors(),
          listPendingApprovalMeasuringInstrumentInspectors(),
        ]);
        setRejectedInspectors(Array.isArray(rejectedList) ? rejectedList : []);
        setPendingApprovalInspectors(Array.isArray(pendingList) ? pendingList : []);
      } catch (error) {
        console.error('Dashboard instrument approval summary fetch failed', error);
        setRejectedInspectors([]);
        setPendingApprovalInspectors([]);
      } finally {
        setIsRejectedLoading(false);
        setIsPendingApprovalLoading(false);
      }
    };
    loadInstrumentApprovalSummaries();
  }, [isAdmin]);

  const rejectedCount = rejectedInspectors.length;
  const pendingApprovalCount = pendingApprovalInspectors.length;

  const renderInstrumentApprovalList = (
    items: MeasuringInstrumentInspector[],
    isLoading: boolean,
    emptyText: string,
    actorLabel: string,
    dateField: 'assignmentAt' | 'rejectedAt',
  ) => (
    <List
      loading={isLoading}
      dataSource={items}
      locale={{ emptyText }}
      renderItem={(item) => {
        const inventory = item?.measuringInstrumentInventory;
        const instrumentName = inventory?.measuringInstrument?.name ?? 'Cihaz bilgisi bulunamadı';
        const inspectorName = item?.account
          ? `${item.account.firstName ?? ''} ${item.account.lastName ?? ''}`.trim()
          : undefined;
        const detailLink = inventory?.id ? `/measuring-instrument-inventories/${inventory.id}` : undefined;
        const actions = detailLink
          ? [
              <Link key="detail-link" to={detailLink}>
                Detay
              </Link>,
            ]
          : [];
        const dateValue = item?.[dateField];
        const listKey = item?.id ?? `${inventory?.id ?? 'unknown'}-${dateValue ?? ''}`;
        return (
          <List.Item key={listKey} actions={actions}>
            <List.Item.Meta
              title={instrumentName}
              description={
                <>
                  <div style={{ color: '#8c8c8c' }}>Envanter #{inventory?.id ?? '—'}</div>
                  {inventory?.serialNumber && (
                    <div style={{ color: '#8c8c8c' }}>Seri No: {inventory.serialNumber}</div>
                  )}
                  {inspectorName && (
                    <div style={{ color: '#8c8c8c' }}>{actorLabel}: {inspectorName}</div>
                  )}
                </>
              }
            />
            <div style={{ minWidth: 120, textAlign: 'right' }}>
              <Text type="secondary">
                {dateValue ? dayjs(dateValue).format(APP_DATE_FORMAT) : 'Tarih yok'}
              </Text>
            </div>
          </List.Item>
        );
      }}
    />
  );

  const renderInstrumentApprovalCard = (
    title: string,
    count: number,
    isLoading: boolean,
    onClick: () => void,
  ) => (
    <Card
      bordered={false}
      hoverable
      className="dashboard-card-clickable"
      role="button"
      tabIndex={0}
      aria-label={title}
      style={{
        background: '#fff',
        textAlign: 'center',
        minHeight: 216,
        width: '100%',
        height: '100%',
        borderRadius: 8,
      }}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
    >
      <div
        style={{
          fontSize: 16,
          color: '#555',
          minHeight: 60,
          fontWeight: 600,
        }}
      >
        {title}
      </div>
      <div
        style={{
          color: count === 0 ? '#52c41a' : '#f5222d',
          fontWeight: 700,
          fontSize: 28,
        }}
      >
        {isLoading ? <Spin size="small" /> : count}
      </div>
    </Card>
  );

  if (isFinance) {
    return <FinanceDashboard />;
  }

  return (
    <div className='p-20px'>
      <Row gutter={[16, 16]}>
        {(!isFinance) && (
          <Col lg={6} md={24} xs={24}>
            <Link to="/proposals/create">
              <Card bordered={false} className="dashboard-card dashboard-card-bg-dark" bodyStyle={{ padding: 10 }}>
                <div className='dashboard-container'>
                  <div className='card-icon'>
                    <PlusOutlined width={20} height={20} />
                  </div>
                  <div>
                    <Title style={{ fontWeight: 'bold', color: "white" }} className='mb-0' level={3}>Teklif Oluşturun</Title>
                    <Text style={{ fontWeight: 300, color: "white", fontSize: 18 }} className="m-0">Gelirinizi Arttırın</Text>
                  </div>
                </div>
              </Card>
            </Link>
          </Col>
        )}
        <Col lg={6} md={24} xs={24}>
          <Link to="/proposals?activeKey=2">
            <SummaryCard />
          </Link>
        </Col>
        <Col lg={6} md={24} xs={24}>
          <Link to="/project-followup">
            <ContractCountCard />
          </Link>
        </Col>
        <Col lg={6} md={24} xs={24}>

          {(isAdmin) &&
            <Link to="/payments" className="vallet-card">
              <RenuewCard />
            </Link>
          }

          {(isCentralUser) &&
            <RenuewCard />}

          {(!isAdmin && !isCentralUser) && <Link to="/wallet" className="vallet-card">
            <RenuewCard />
          </Link>}
        </Col>

      </Row>

      {/* Teklifler Bölümü */}
      <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
        <Col lg={14}>
          <Card style={{ height: 540 }} className='chart-card'>
            <ProposalChart />
          </Card>
        </Col>
        <Col lg={10}>
          <Card style={{ height: 260, marginBottom: 20 }} className='chart-card'>
            <JobTrackingChart />
          </Card>
          {(isAdmin || isCentralUser) && <TicketWidget />}
          {(!isAdmin && !isCentralUser) && <Card style={{ height: 260 }} className='chart-card'>
            <RenuewChart />
          </Card>}
        </Col>
      </Row>

      {(isAdmin || isCentralUser) && (
        <StatusSummaryCards />
      )}

      {(isAdmin || isCentralUser) && (
        <Row gutter={[24, 24]} role="region" aria-label="Cihaz Takibi" style={{ marginTop: '24px' }}>
          {isAdmin && (
            <>
              <Col xs={24} lg={6} style={{ display: 'flex' }}>
                {renderInstrumentApprovalCard(
                  'Onay Bekleyen Cihazlar', pendingApprovalCount, isPendingApprovalLoading,
                  () => setIsPendingApprovalModalOpen(true),
                )}
              </Col>
              <Col xs={24} lg={6} style={{ display: 'flex' }}>
                {renderInstrumentApprovalCard(
                  'Reddedilen Cihazlar', rejectedCount, isRejectedLoading,
                  () => setIsRejectedModalOpen(true),
                )}
              </Col>
            </>
          )}
          <Col xs={24} lg={isAdmin ? 12 : 24} style={{ display: 'flex' }}>
            <PlanDeviceReadinessCard />
          </Col>
        </Row>
      )}

      {canViewLegalNotificationSummary && (
        <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
          <Col xs={12}>
            <LegalNotificationSummaryCard />
          </Col>
           {!isCustomer && (
          <Col xs={24} lg={12}>
            <ReminderSummaryCards
              title="Beklemede Olan Görevler"
              mode={isAdmin ? 'admin' : 'me'}
            />
          </Col>
      )}
        </Row>
      )}

     

      {isAdmin && (
        <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
          <Col xs={24} sm={24} md={24} lg={12} xl={12}>
            <OverdueSentProposalsList />
          </Col>
          <Col xs={24} sm={24} md={24} lg={12} xl={12}>
            <ManualFollowProposalsList />
          </Col>
        </Row>
      )}

      <Drawer
        title="Onay Bekleyen Ölçüm Cihazları"
        open={isPendingApprovalModalOpen}
        width={720}
        onClose={() => setIsPendingApprovalModalOpen(false)}
        placement="right"
      >
        {renderInstrumentApprovalList(
          pendingApprovalInspectors,
          isPendingApprovalLoading,
          'Onay bekleyen cihaz bulunamadı',
          'Onay Bekleyen PKM Uzmanı',
          'assignmentAt',
        )}
      </Drawer>

      <Drawer
        title="Reddedilen Ölçüm Cihazları"
        open={isRejectedModalOpen}
        width={720}
        onClose={() => setIsRejectedModalOpen(false)}
        placement="right"
      >
        {renderInstrumentApprovalList(
          rejectedInspectors,
          isRejectedLoading,
          'Reddedilen cihaz bulunamadı',
          'Reddeden',
          'rejectedAt',
        )}
      </Drawer>



    </div>
  );
};

export default Dashboard;
